import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import Team from "@/pages/Team";
import Suppliers from "@/pages/Suppliers";
import PaymentMethods from "@/pages/PaymentMethods";
import CompanyInfo from "@/pages/CompanyInfo";
import {
  Users, Package, FileText, Truck, UserCog, Settings as SettingsIcon,
  ShieldCheck, TrendingUp, CreditCard, BarChart3, Receipt,
} from "lucide-react";

export default function ControlPanel() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  const bigCards = [
    { key: "totalTeam", value: stats?.users ?? 0, icon: UserCog, color: "text-indigo-700 bg-indigo-50" },
    { key: "totalSuppliers", value: stats?.suppliers ?? 0, icon: Truck, color: "text-orange-700 bg-orange-50" },
    { key: "totalClients", value: stats?.clients ?? 0, icon: Users, color: "text-blue-700 bg-blue-50" },
    { key: "totalPrograms", value: stats?.programs ?? 0, icon: Package, color: "text-emerald-700 bg-emerald-50" },
    { key: "totalBookings", value: stats?.bookings ?? 0, icon: FileText, color: "text-purple-700 bg-purple-50" },
    { key: "totalRevenue", value: `$${(stats?.revenue ?? 0).toLocaleString()}`, icon: TrendingUp, color: "text-amber-700 bg-amber-50" },
  ];

  const quickLinks = [
    { to: "/reports", label: "salesReport", icon: BarChart3 },
    { to: "/settings", label: "manageSettings", icon: SettingsIcon },
  ];

  return (
    <div className="space-y-8" data-testid="control-panel-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-label flex items-center gap-2">
            <ShieldCheck size={14} className="text-[hsl(var(--accent))]" />
            {t("controlPanel")}
          </div>
          <h1 className="display-font text-3xl md:text-4xl font-extrabold text-[hsl(var(--primary))] mt-1">
            {t("controlPanel")}
          </h1>
          <div className="mt-3 divider-accent" />
        </div>
        <div className="flex flex-wrap gap-2">
          {quickLinks.map((q) => (
            <Button key={q.to} asChild variant="outline" size="sm" data-testid={`quick-${q.label}`}>
              <Link to={q.to}>
                <q.icon size={15} className="me-2" />
                {t(q.label)}
              </Link>
            </Button>
          ))}
        </div>
      </div>

      {/* Platform stats grid */}
      <div>
        <div className="uppercase-label mb-3">{t("platformStats")}</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {bigCards.map((c) => (
            <div key={c.key} className="dashboard-card fade-up" data-testid={`cp-stat-${c.key}`}>
              <div className={"w-10 h-10 rounded-lg grid place-items-center mb-3 " + c.color}>
                <c.icon size={18} />
              </div>
              <div className="uppercase-label">{t(c.key)}</div>
              <div className="stat-value mt-1">{c.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs: Company info + Team + Suppliers + Payment Methods */}
      <div className="dashboard-card">
        <Tabs defaultValue="company">
          <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2 sm:grid-cols-4 mb-4">
            <TabsTrigger value="company" data-testid="cp-tab-company">
              <Receipt size={15} className="me-2" />
              {t("companyInfo")}
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="cp-tab-team">
              <UserCog size={15} className="me-2" />
              {t("manageTeam")}
            </TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="cp-tab-suppliers">
              <Truck size={15} className="me-2" />
              {t("manageSuppliers")}
            </TabsTrigger>
            <TabsTrigger value="pm" data-testid="cp-tab-pm">
              <CreditCard size={15} className="me-2" />
              {t("paymentMethods")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="company" className="mt-2">
            <CompanyInfo />
          </TabsContent>
          <TabsContent value="team" className="mt-2">
            <Team embedded />
          </TabsContent>
          <TabsContent value="suppliers" className="mt-2">
            <Suppliers embedded />
          </TabsContent>
          <TabsContent value="pm" className="mt-2">
            <PaymentMethods />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
